import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  BASE_OFFERS,
  offerColor,
  useAllOffers,
  type Offer,
} from "../useOffers";
import { stageLabel } from "./useOfferStages";

export const DEFAULT_STAGES =
  "Nuevo, Contactado, Conversación, Demo, Propuesta, Ganado, Perdido";
/** Normaliza un nombre a slug (sin acentos, solo a-z0-9) — mismo criterio para el slug de
 * `offers` en el formulario de siempre y en el asistente (wizard/OfferWizard.tsx). */
export const plain = (t: string) =>
  t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9]/g, "");
// Etiqueta escrita → valor de etapa: las conocidas usan su clave (nuevo, demo…);
// las nuevas se guardan tal cual (con acentos) y así se muestran.
const KNOWN = [
  "nuevo",
  "contactado",
  "conversacion",
  "interesado",
  "entrevista",
  "diagnostico",
  "demo",
  "piloto",
  "preventa",
  "propuesta",
  "onboarding",
  "activo",
  "ganado",
  "perdido",
];
export const toStage = (label: string) =>
  KNOWN.find((k) => k === plain(label)) ?? label;

/** Etapas escritas separadas por coma → lista limpia + cuál es "ganado"/"perdido" (regla
 * compartida con el asistente de Bloque N, wizard/OfferWizard.tsx). */
export const parseStages = (raw: string) => {
  const list = [
    ...new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(toStage),
    ),
  ];
  const won = list.includes("ganado")
    ? "ganado"
    : list.includes("activo")
      ? "activo"
      : list[list.length - 2];
  const lost = list.includes("perdido") ? "perdido" : list[list.length - 1];
  return { list, won, lost };
};

export type OfferFormTarget =
  // desde el Radar: nombre sugerido y aviso al crear (para ligar la idea con su tablero)
  | { mode: "create"; name?: string; onCreated?: (id: number) => void }
  | { mode: "edit"; offer: Offer };

/** Crear, editar, archivar o eliminar un tablero de producto (PRD B.3). */
export const OfferForm = ({
  target,
  onClose,
}: {
  target: OfferFormTarget | null;
  onClose: () => void;
}) => {
  const { data: all } = useAllOffers();
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const offer = target?.mode === "edit" ? target.offer : null;
  const isBase = !!offer && BASE_OFFERS.includes(offer.slug);

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [color, setColor] = useState("#0EA5A4");
  const [priority, setPriority] = useState(2);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dealCount, setDealCount] = useState(0);

  useEffect(() => {
    if (!target) return;
    setConfirmDelete(false);
    if (offer) {
      setName(offer.name);
      setKey(offer.key ?? "");
      setColor(offerColor(offer));
      setPriority(offer.priority);
      setStages(offer.stages.map(stageLabel).join(", "));
      getSupabaseClient()
        .from("deals")
        .select("id", { count: "exact", head: true })
        .eq("offer_id", offer.id)
        .then(({ count }) => setDealCount(count ?? 0));
    } else {
      setName(target.mode === "create" ? (target.name ?? "") : "");
      setKey("");
      setColor("#0EA5A4");
      setPriority(2);
      setStages(DEFAULT_STAGES);
    }
  }, [target, offer]);

  if (!target) return null;

  const refresh = () => {
    ["offers", "funnel_by_offer", "deal_board"].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }),
    );
  };

  const submit = async () => {
    const k = key.toUpperCase().replace(/[^A-Z]/g, "");
    const { list, won, lost } = parseStages(stages);
    if (!name.trim()) return notify("Escribe el nombre", { type: "error" });
    if (k.length < 2 || k.length > 4)
      return notify("La clave debe tener de 2 a 4 letras", { type: "error" });
    if (all?.some((o) => o.id !== offer?.id && o.key === k))
      return notify("Esa clave ya la usa otro tablero", { type: "error" });
    if (list.length < 2)
      return notify("Agrega al menos dos etapas", { type: "error" });
    const data = {
      name: name.trim(),
      key: k,
      color,
      priority,
      stages: list,
      won_stage: won,
      lost_stage: lost,
    };
    const sb = getSupabaseClient();
    if (offer) {
      const { error } = await sb.from("offers").update(data).eq("id", offer.id);
      if (error) return notify(error.message, { type: "error" });
      // oportunidades en etapas que ya no existen pasan a la primera
      await sb
        .from("deals")
        .update({ stage: list[0] })
        .eq("offer_id", offer.id)
        .not("stage", "in", `(${list.map((s) => `"${s}"`).join(",")})`);
      notify("Tablero actualizado", { type: "success" });
    } else {
      let slug = plain(name) || "oferta";
      while (all?.some((o) => o.slug === slug)) slug += "2";
      const { data: created, error } = await sb
        .from("offers")
        .insert({ ...data, slug })
        .select("id")
        .single();
      if (error) return notify(error.message, { type: "error" });
      notify(`Tablero creado: ${name}`, { type: "success" });
      if (target.mode === "create") target.onCreated?.(created.id);
      navigate(`/board/${created.id}`);
    }
    refresh();
    onClose();
  };

  const toggleArchive = async () => {
    if (!offer) return;
    await getSupabaseClient()
      .from("offers")
      .update({ active: !offer.active })
      .eq("id", offer.id);
    notify(
      offer.active
        ? `${offer.name} archivado`
        : `${offer.name} visible de nuevo`,
      { type: "success" },
    );
    refresh();
    onClose();
    if (offer.active) navigate("/");
  };

  const remove = async () => {
    if (!offer) return;
    const sb = getSupabaseClient();
    // sus oportunidades (y sus tareas, por cascada) se van con el tablero
    const { error: e1 } = await sb
      .from("deals")
      .delete()
      .eq("offer_id", offer.id);
    const { error: e2 } = e1
      ? { error: e1 }
      : await sb.from("offers").delete().eq("id", offer.id);
    if (e2) return notify(e2.message, { type: "error" });
    notify(`${offer.name} eliminado`, { type: "success" });
    refresh();
    onClose();
    navigate("/");
  };

  const fieldCls = "h-10 w-full rounded-lg border bg-card px-3 text-sm";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {confirmDelete && offer ? (
          <>
            <DialogHeader>
              <DialogTitle>¿Eliminar {offer.name}?</DialogTitle>
              <DialogDescription>
                Se eliminan su tablero y {dealCount} oportunidad
                {dealCount === 1 ? "" : "es"} con sus tareas. No se puede
                deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={remove}>
                Eliminar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {offer ? `Editar ${offer.name}` : "Nuevo producto o servicio"}
              </DialogTitle>
              <DialogDescription>
                {offer
                  ? "Tablero de oportunidades"
                  : "Se agrega al menú Oportunidades con su propio tablero"}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1 text-sm font-semibold">
                Nombre
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej. Respuestas a reseñas con IA"
                />
              </label>
              <label className="grid gap-1 text-sm font-semibold">
                Clave (2 a 4 letras)
                <Input
                  value={key}
                  maxLength={4}
                  onChange={(e) => setKey(e.target.value.toUpperCase())}
                  placeholder="REP"
                />
              </label>
              <label className="grid gap-1 text-sm font-semibold">
                Color
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-full rounded-lg border bg-card p-1"
                />
              </label>
              <label className="grid gap-1 text-sm font-semibold">
                Prioridad
                <select
                  className={fieldCls}
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                >
                  <option value={1}>1 · Principal</option>
                  <option value={2}>2 · Secundaria</option>
                  <option value={3}>3 · Complementaria</option>
                </select>
              </label>
              <label className="grid gap-1 text-sm font-semibold sm:col-span-2">
                Etapas, en orden y separadas por coma
                <Input
                  value={stages}
                  onChange={(e) => setStages(e.target.value)}
                />
                <span className="text-xs font-normal text-muted-foreground">
                  Termina con Ganado y Perdido para que cuenten tus tasas de
                  cierre.
                </span>
              </label>
            </div>
            {isBase && (
              <p className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
                Es uno de tus 7 productos base: el directorio está segmentado
                por él, por eso se archiva en lugar de eliminarse.
              </p>
            )}
            <DialogFooter className="sm:justify-between">
              {offer ? (
                isBase ? (
                  <Button variant="outline" onClick={toggleArchive}>
                    {offer.active ? "Archivar" : "Mostrar en el menú"}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => setConfirmDelete(true)}
                  >
                    Eliminar
                  </Button>
                )
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button onClick={submit}>
                  {offer ? "Guardar" : "Crear tablero"}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
